/**
 * What a CLIENT sees. Not a smaller dashboard - a different product.
 *
 * The person reading this is the customer whose servers these are. They did not
 * buy backup software, they bought "my data is safe", and they cannot evaluate
 * a vendor error string. So this page answers three questions and refuses to
 * raise a fourth:
 *
 *   are my machines backed up          the headline
 *   which ones are not                 the list
 *   is this getting worse              thirty days
 *
 * EVERYTHING AT ONCE, THEN NARROWED. An owner holding nine practices sees all
 * nine together and filters down; they do not click through nine pages. The
 * first version of this had a switcher and showed one site at a time, on the
 * reasoning that "3 of 47 need attention" tells a practice manager nothing
 * about their own surgery. That reasoning was sound and the conclusion was
 * wrong: the person with several sites is usually the owner, and the owner's
 * first question is about the group. The filters answer the manager's question
 * without making the owner work for theirs. Damon: "not having a switcher but a
 * portal that shows all or by org. and by backup type."
 *
 * WHAT IS DELIBERATELY ABSENT, and none of it is an oversight:
 *
 *   - reason strings. "Fatal I/O error on smb://... sbos_win.c:1248" is true,
 *     unreadable, and alarming in a way the reader cannot act on. It would
 *     generate the call this portal exists to prevent.
 *   - any other customer, ever.
 *   - anything writable. There is no control on this page that changes
 *     anything, so there is no path from a client session to your data.
 *
 * That is not enforced by this file being careful. It is enforced by the SHAPE
 * OF THE DOCUMENT: orgs/{orgId}/portal/{clientId} is derived and contains only
 * name, status, date, backup type and thirty day-counts. A mistake here leaks a
 * machine name and a date, because that is all there is. See portal.ts.
 */

import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import { Wordmark } from './brand';
import { useLogoState } from './branding';

type PortalStatus = 'ok' | 'late' | 'failed' | 'paused' | 'unknown';

interface PortalMachine {
  id: string;
  name: string;
  status: PortalStatus;
  lastBackup: number | null;
  source?: string;
}

interface PortalDay { day: string; ok: number; bad: number }

interface PortalDoc {
  client: string;
  orgName: string;
  machines: PortalMachine[];
  total: number;
  protected: number;
  history: PortalDay[];
  builtAt: number;
}

/** A machine, plus which of the reader's sites it belongs to. */
interface Row extends PortalMachine {
  clientId: string;
  clientName: string;
}

const STATUS_TEXT: Record<PortalStatus, string> = {
  ok: 'Backed up',
  late: 'Backed up, later than planned',
  failed: 'Not backed up',
  paused: 'Paused',
  unknown: 'Being checked',
};

const STATUS_CLASS: Record<PortalStatus, string> = {
  ok: 'p-ok', late: 'p-late', failed: 'p-bad', paused: 'p-paused', unknown: 'p-unknown',
};

/**
 * Backup types, in the reader's language rather than ours.
 *
 * "Image Manager" and "ShadowProtect" are two halves of one product to somebody
 * who bought neither. NO VENDOR NAMES, confirmed by Damon 2026-08-12: a client
 * who can read the product list can price-shop it or go direct.
 *
 * The distinction a client can actually act on is WHERE THE COPY LIVES - on
 * their premises, or somewhere else if the building burns down. So the labels
 * answer that and nothing else. "Backup copies" was replaced because it said
 * nothing at all; "Appliance backup" because "appliance" is our word, not theirs.
 *
 * SEVERAL SOURCES MAY SHARE A LABEL, and that is intended - a customer does not
 * care that two products do the same job. Everything downstream therefore keys
 * off the LABEL, never the source id. See `types` below for why that matters.
 */
const SOURCE_TEXT: Record<string, string> = {
  datto: 'On-site backup',
  unitrends: 'On-site backup',
  sp: 'Server backup',
  sc: 'Off-site copy',
  msp: 'Cloud backup',
  email: 'Backup',
};
const sourceLabel = (s: string | undefined): string => SOURCE_TEXT[s ?? ''] ?? 'Backup';

/** Worst first. Somebody scanning this page should meet the problems first. */
const ORDER: Record<PortalStatus, number> = { failed: 0, unknown: 1, late: 2, ok: 3, paused: 4 };

function when(ms: number | null): string {
  if (!ms) return 'No backup recorded yet';
  const hours = (Date.now() - ms) / 3_600_000;
  const stamp = new Date(ms).toLocaleString(undefined, {
    weekday: 'short', hour: 'numeric', minute: '2-digit', day: 'numeric', month: 'short',
  });
  if (hours < 1) return `Less than an hour ago - ${stamp}`;
  if (hours < 24) return `${Math.round(hours)} hours ago - ${stamp}`;
  return `${Math.round(hours / 24)} days ago - ${stamp}`;
}

/**
 * Thirty days across every site the reader can see.
 *
 * Summed rather than shown per site: the question this answers is "is this
 * getting worse", and thirty squares repeated nine times answers it worse than
 * thirty squares once.
 */
function History({ docs }: { docs: PortalDoc[] }) {
  const days = useMemo(() => {
    const merged = new Map<string, { ok: number; bad: number }>();
    for (const d of docs) {
      for (const day of d.history ?? []) {
        const cur = merged.get(day.day) ?? { ok: 0, bad: 0 };
        merged.set(day.day, { ok: cur.ok + day.ok, bad: cur.bad + day.bad });
      }
    }
    return [...merged.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [docs]);

  if (!days.length) return null;

  return (
    <section className="portal-card">
      <h2 className="portal-h2">The last thirty days</h2>
      <p className="portal-sub">
        Each square is a day. Amber means something needed attention that day; it does not
        mean data was lost.
      </p>
      <div className="portal-days">
        {days.map(([day, d]) => (
          <span
            key={day}
            className={'portal-day' + (d.bad > 0 ? ' portal-day-bad' : '')}
            title={
              d.bad > 0
                ? `${day}: ${d.bad} backup${d.bad === 1 ? '' : 's'} needed attention`
                : `${day}: nothing needed attention`
            }
          />
        ))}
      </div>
    </section>
  );
}

export function ClientPortal({ orgId, clientIds }: { orgId: string; clientIds: string[] }) {
  /*
   * ONE SUBSCRIPTION PER SITE, held in a map keyed by client id.
   *
   * Live rather than fetched once: somebody watching while an engineer fixes a
   * server should see it go green rather than wonder whether to refresh. A
   * missing document is simply an absent entry - a site whose summary has not
   * been built yet must not blank the sites that have.
   */
  const [docs, setDocs] = useState<Record<string, PortalDoc>>({});
  const [failed, setFailed] = useState<Record<string, true>>({});
  const [ready, setReady] = useState(false);
  const { src: logoSrc, isDefault: logoIsDefault } = useLogoState();

  const [site, setSite] = useState('all');
  const [type, setType] = useState('all');

  useEffect(() => {
    const seen = new Set<string>();
    const unsubs = clientIds.map((id) =>
      onSnapshot(
        doc(db, 'orgs', orgId, 'portal', id),
        (snap) => {
          seen.add(id);
          if (snap.exists()) setDocs((m) => ({ ...m, [id]: snap.data() as PortalDoc }));
          else setFailed((m) => ({ ...m, [id]: true }));
          if (seen.size >= clientIds.length) setReady(true);
        },
        () => {
          seen.add(id);
          setFailed((m) => ({ ...m, [id]: true }));
          if (seen.size >= clientIds.length) setReady(true);
        },
      ),
    );
    return () => unsubs.forEach((u) => u());
  }, [orgId, clientIds.join(',')]);

  const loaded = Object.values(docs);
  const orgName = loaded[0]?.orgName ?? '';

  /** Every machine the reader can see, tagged with which site it is at. */
  const allRows: Row[] = useMemo(
    () =>
      Object.entries(docs).flatMap(([clientId, d]) =>
        (d.machines ?? []).map((m) => ({ ...m, clientId, clientName: d.client })),
      ),
    [docs],
  );

  const sites = useMemo(
    () => Object.entries(docs).map(([id, d]) => ({ id, name: d.client })).sort(
      (a, b) => a.name.localeCompare(b.name),
    ),
    [docs],
  );

  /*
   * BY LABEL, NOT BY SOURCE, and this was a live bug rather than a tidy-up.
   *
   * Datto and Unitrends have always shared a label. Keyed on the source id, a
   * customer running both saw the same wording listed twice in the dropdown,
   * and choosing either one silently hid the other's machines - a filter that
   * appears to show "all of this type" while omitting half of it.
   */
  const types = useMemo(() => {
    const present = new Set(allRows.map((r) => sourceLabel(r.source)));
    return [...present].filter(Boolean).sort();
  }, [allRows]);

  const rows = useMemo(
    () =>
      allRows
        .filter((r) => site === 'all' || r.clientId === site)
        .filter((r) => type === 'all' || sourceLabel(r.source) === type)
        .sort(
          (a, b) =>
            (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) ||
            a.clientName.localeCompare(b.clientName) ||
            a.name.localeCompare(b.name),
        ),
    [allRows, site, type],
  );

  const problems = rows.filter((r) => r.status === 'failed' || r.status === 'unknown');
  const counted = rows.filter((r) => r.status !== 'paused');
  const allWell = problems.length === 0;
  const signOutNow = () => void signOut(auth);

  if (!ready) return <div className="boot">Loading...</div>;

  if (loaded.length === 0) {
    return (
      <div className="portal">
        <div className="portal-card portal-empty">
          <h1 className="portal-h1">Nothing to show yet</h1>
          <p className="portal-sub">
            Your backup summary has not been prepared yet. It is built automatically and should
            appear shortly.
          </p>
          <button className="portal-btn" onClick={signOutNow}>Sign out</button>
        </div>
      </div>
    );
  }

  const several = sites.length > 1;

  return (
    <div className="portal">
      <header className="portal-head">
        {logoIsDefault
          ? <Wordmark width={170} />
          : <img className="portal-logo" src={logoSrc} alt={orgName} />}
        <button className="portal-signout" onClick={signOutNow}>Sign out</button>
      </header>

      <section className={'portal-card portal-headline ' + (allWell ? 'portal-good' : 'portal-attention')}>
        <h1 className="portal-h1">
          {allWell
            ? 'Your backups are up to date'
            : `${problems.length} of your ${counted.length} machines need attention`}
        </h1>
        <p className="portal-sub">
          {allWell
            ? `All ${counted.length} machines${several && site === 'all' ? ` across ${sites.length} sites` : ''} are being backed up. ${orgName} is monitoring them continuously.`
            : `${orgName} has been notified and is working on it. The rest are backing up normally.`}
        </p>
      </section>

      {/* Filters, only when there is something to filter. A control that can
          only ever say "all" is a control that costs attention and gives
          nothing back. */}
      {(several || types.length > 1) && (
        <div className="portal-filters">
          {several && (
            <label className="portal-filter">
              <span className="portal-filter-label">Site</span>
              <select className="portal-select" value={site} onChange={(e) => setSite(e.target.value)}>
                <option value="all">All {sites.length} sites</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          )}
          {types.length > 1 && (
            <label className="portal-filter">
              <span className="portal-filter-label">Type</span>
              <select className="portal-select" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="all">All types</option>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          )}
        </div>
      )}

      <section className="portal-card">
        <h2 className="portal-h2">
          {site === 'all' ? 'Your machines' : sites.find((s) => s.id === site)?.name ?? 'Your machines'}
        </h2>
        {rows.length === 0 ? (
          <p className="portal-sub" style={{ marginTop: 10 }}>
            Nothing matches that combination.
          </p>
        ) : (
          <table className="portal-table">
            <tbody>
              {rows.map((m) => (
                <tr key={m.clientId + m.id}>
                  <td className="portal-name">
                    {m.name}
                    {/* The site, only when several are in view - repeating it
                        down a single-site list is noise. */}
                    {several && site === 'all' && (
                      <div className="portal-site">{m.clientName}</div>
                    )}
                  </td>
                  <td className="portal-type">{sourceLabel(m.source)}</td>
                  <td>
                    <span className={'portal-pill ' + STATUS_CLASS[m.status]}>
                      {STATUS_TEXT[m.status] ?? 'Being checked'}
                    </span>
                  </td>
                  <td className="portal-when">{when(m.lastBackup)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <History docs={loaded} />

      <p className="portal-foot">
        Prepared for {several ? `${sites.length} sites` : loaded[0]?.client} by {orgName}. Updated{' '}
        {new Date(Math.max(...loaded.map((d) => d.builtAt ?? 0))).toLocaleString()}.
        {Object.keys(failed).length > 0 && ' One or more sites are still being prepared.'}
        {' '}Questions about anything on this page should go to {orgName}.
      </p>
    </div>
  );
}
